import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Hazard,
  HazardStatus,
  HazardSeverity,
  CauseType,
  ConsequenceSeverity,
  ControlType,
  ControlEffectiveness,
  SourceType,
  ReviewVerdict,
} from '../types';
import { hazardService } from '../hazardService';

interface HazardDetailModalProps {
  hazardId: string;
  onClose: () => void;
  onUpdated: () => void;
  onOpenBowtie?: (riskId: string) => void;
}

type TabType =
  | 'overview'
  | 'causes_consequences'
  | 'controls'
  | 'sources'
  | 'reviews'
  | 'collaboration'
  | 'history';

export const HazardDetailModal: React.FC<HazardDetailModalProps> = ({
  hazardId,
  onClose,
  onUpdated,
  onOpenBowtie,
}) => {
  const [hazard, setHazard] = useState<Hazard | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sub-forms state
  const [newCauseText, setNewCauseText] = useState('');
  const [newCauseType, setNewCauseType] = useState<CauseType>('HUMAN');

  const [newConsequenceText, setNewConsequenceText] = useState('');
  const [newConsequenceSeverity, setNewConsequenceSeverity] = useState<ConsequenceSeverity>('MAJOR');

  const [newControlTitle, setNewControlTitle] = useState('');
  const [newControlType, setNewControlType] = useState<ControlType>('PREVENTIVE');
  const [newControlDesc, setNewControlDesc] = useState('');
  const [newControlEffectiveness, setNewControlEffectiveness] = useState<ControlEffectiveness>('SATISFACTORY');
  const [newControlDept, setNewControlDept] = useState('');

  const [newSourceType, setNewSourceType] = useState<SourceType>('OCCURRENCE');
  const [newSourceRef, setNewSourceRef] = useState('');
  const [newSourceDesc, setNewSourceDesc] = useState('');

  const [reviewVerdict, setReviewVerdict] = useState<ReviewVerdict>('MAINTAIN_CONTROLS');
  const [reviewComments, setReviewComments] = useState('');
  const [customReviewDays, setCustomReviewDays] = useState(90);

  const [commentText, setCommentText] = useState('');
  const [statusReason, setStatusReason] = useState('');

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await hazardService.getHazardById(hazardId);
      setHazard(data);
    } catch (err: any) {
      console.error('Failed to load hazard:', err);
      setError(err.message || 'Failed to load hazard dossier');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [hazardId]);

  const handleTransition = async (toStatus: HazardStatus) => {
    const reason = prompt(`Enter formal rationale to transition status to ${toStatus}:`);
    if (!reason || reason.trim().length < 5) {
      alert('A formal justification with at least 5 characters is required.');
      return;
    }
    try {
      await hazardService.transitionStatus(hazardId, toStatus, reason.trim());
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      alert(`Status transition failed: ${err.message}`);
    }
  };

  const handleAddCause = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCauseText.trim()) return;
    try {
      await hazardService.addCause(hazardId, newCauseText.trim(), newCauseType);
      setNewCauseText('');
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleRemoveCause = async (id: string) => {
    if (!confirm('Remove this cause?')) return;
    try {
      await hazardService.removeCause(hazardId, id);
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddConsequence = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newConsequenceText.trim()) return;
    try {
      await hazardService.addConsequence(hazardId, newConsequenceText.trim(), newConsequenceSeverity);
      setNewConsequenceText('');
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleRemoveConsequence = async (id: string) => {
    if (!confirm('Remove this consequence?')) return;
    try {
      await hazardService.removeConsequence(hazardId, id);
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddControl = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newControlTitle.trim() || !newControlDesc.trim()) return;
    try {
      await hazardService.addControl(
        hazardId,
        newControlTitle.trim(),
        newControlType,
        newControlDesc.trim(),
        newControlEffectiveness,
        newControlDept.trim() || undefined
      );
      setNewControlTitle('');
      setNewControlDesc('');
      setNewControlDept('');
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleToggleControl = async (controlId: string, currentActive: boolean) => {
    try {
      await hazardService.updateControlStatus(hazardId, controlId, !currentActive);
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSourceRef.trim()) return;
    try {
      await hazardService.linkSource(hazardId, newSourceType, newSourceRef.trim(), newSourceDesc.trim() || undefined);
      setNewSourceRef('');
      setNewSourceDesc('');
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleUnlinkSource = async (id: string) => {
    if (!confirm('Unlink this source?')) return;
    try {
      await hazardService.unlinkSource(hazardId, id);
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleRecordReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewComments.trim()) return;
    try {
      await hazardService.recordReview(
        hazardId,
        'USR-CHIEF-01',
        'Chief Inspector Demir',
        reviewVerdict,
        reviewComments.trim(),
        customReviewDays
      );
      setReviewComments('');
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      await hazardService.addComment(hazardId, commentText.trim(), 'USR-SAFETY-01', 'Safety Analyst');
      setCommentText('');
      await fetchDetail();
    } catch (err: any) {
      alert(err.message);
    }
  };

  if (loading && !hazard) {
    return null;
  }

  if (error || !hazard) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: '#fff', padding: '28px 32px', borderRadius: '8px', maxWidth: '420px', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ color: '#dc2626', fontWeight: 700, fontSize: '16px', marginBottom: '8px' }}>Unable to Load Hazard</div>
          <p style={{ fontSize: '13px', color: '#475569', marginBottom: '16px', lineHeight: 1.4 }}>{error || 'Hazard dossier could not be found.'}</p>
          <button className="btn-secondary" onClick={onClose} style={{ padding: '8px 24px' }}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-5xl" style={{ height: '92vh' }}>
        {/* Top Header */}
        <div
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
            background: '#f8fafc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '18px', fontWeight: 700, color: '#0284c7' }}>
              {hazard.hazardNumber}
            </span>
            <span
              style={{
                background: '#e0f2fe',
                color: '#0369a1',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 700,
              }}
            >
              {hazard.categoryCode || 'GEN'}
            </span>
            <span
              style={{
                background: '#f1f5f9',
                color: '#0f172a',
                border: '1px solid #cbd5e1',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 700,
              }}
            >
              STATUS: {hazard.status}
            </span>
            {hazard.initialRiskIndex && (
              <span
                style={{
                  background: '#e0e7ff',
                  color: '#3730a3',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 700,
                }}
              >
                ICAO RISK: {hazard.initialRiskIndex}
              </span>
            )}
            {onOpenBowtie && hazard.linkedRisks && hazard.linkedRisks.length > 0 && (
              <button
                onClick={() => {
                  onClose();
                  onOpenBowtie(hazard.linkedRisks![0].id);
                }}
                style={{
                  background: '#059669',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '5px 12px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
                title="Open Bowtie Visual Diagram for this Hazard's Linked Risk"
              >
                🎀 Bowtie Diagram ({hazard.linkedRisks[0].riskNumber})
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Quick Status Action Controls */}
            {hazard.status === 'DRAFT' && (
              <button
                onClick={() => handleTransition('IDENTIFIED')}
                style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                Identify
              </button>
            )}
            {hazard.status === 'IDENTIFIED' && (
              <>
                <button
                  onClick={() => handleTransition('UNDER_REVIEW')}
                  style={{ background: '#d97706', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Send for Review
                </button>
                <button
                  onClick={() => handleTransition('ACTIVE')}
                  style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Activate Hazard
                </button>
              </>
            )}
            {hazard.status === 'UNDER_REVIEW' && (
              <>
                <button
                  onClick={() => handleTransition('ACTIVE')}
                  style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Activate
                </button>
                <button
                  onClick={() => handleTransition('MITIGATED')}
                  style={{ background: '#16a34a', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Mark Mitigated
                </button>
              </>
            )}
            {hazard.status === 'ACTIVE' && (
              <>
                <button
                  onClick={() => handleTransition('MITIGATED')}
                  style={{ background: '#16a34a', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Mitigate
                </button>
                <button
                  onClick={() => handleTransition('ACCEPTED')}
                  style={{ background: '#059669', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Accept Risk
                </button>
                <button
                  onClick={() => handleTransition('RETIRED')}
                  style={{ background: '#7e22ce', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Retire
                </button>
              </>
            )}
            {hazard.status === 'MITIGATED' && (
              <button
                onClick={() => handleTransition('CLOSED')}
                style={{ background: '#334155', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                Close Hazard
              </button>
            )}

            <button
              onClick={onClose}
              className="modal-close-btn"
              title="Kapat"
              aria-label="Kapat"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', background: '#ffffff', borderBottom: '1px solid #e2e8f0', padding: '0 20px' }}>
          {[
            { id: 'overview', label: '1. Statement & Overview' },
            { id: 'causes_consequences', label: `2. Causes & Consequences (${(hazard.causes?.length || 0) + (hazard.consequences?.length || 0)})` },
            { id: 'controls', label: `3. Controls & Barriers (${hazard.controls?.length || 0})` },
            { id: 'sources', label: `4. Linked Sources (${hazard.sources?.length || 0})` },
            { id: 'reviews', label: `5. Periodic Reviews (${hazard.reviews?.length || 0})` },
            { id: 'collaboration', label: `6. Notes & Files (${(hazard.comments?.length || 0) + (hazard.attachments?.length || 0)})` },
            { id: 'history', label: '7. Audit & Transitions' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #0284c7' : '2px solid transparent',
                color: activeTab === tab.id ? '#0284c7' : '#64748b',
                padding: '12px 14px',
                fontSize: '13px',
                fontWeight: activeTab === tab.id ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', background: '#f8fafc' }}>
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ background: '#ffffff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#0f172a' }}>{hazard.title}</h3>

                {hazard.linkedRisks && hazard.linkedRisks.length > 0 && (
                  <div
                    style={{
                      background: '#ecfdf5',
                      border: '1px solid #a7f3d0',
                      borderRadius: '8px',
                      padding: '14px 18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '12px',
                      marginBottom: '16px',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Linked Risk Scenario & Bowtie Model
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#065f46', marginTop: '2px' }}>
                        {hazard.linkedRisks[0].riskNumber}: {hazard.linkedRisks[0].title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#047857', marginTop: '2px' }}>
                        Status: {hazard.linkedRisks[0].status} • Initial Risk: {hazard.linkedRisks[0].initialRiskIndex || 'N/A'}
                      </div>
                    </div>
                    {onOpenBowtie && (
                      <button
                        onClick={() => {
                          onClose();
                          onOpenBowtie(hazard.linkedRisks![0].id);
                        }}
                        style={{
                          background: '#059669',
                          color: '#fff',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        }}
                      >
                        🎀 Open Interactive Bowtie Diagram
                      </button>
                    )}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px', marginBottom: '20px' }}>
                  <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '6px', borderLeft: '4px solid #0284c7' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#0284c7', textTransform: 'uppercase' }}>
                      Condition Statement (Existing Threat / Defect)
                    </div>
                    <div style={{ fontSize: '14px', color: '#1e293b', marginTop: '4px', lineHeight: 1.5 }}>
                      {hazard.conditionText}
                    </div>
                  </div>

                  <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '6px', borderLeft: '4px solid #ea580c' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#ea580c', textTransform: 'uppercase' }}>
                      Consequence Statement (Immediate Operational Chain)
                    </div>
                    <div style={{ fontSize: '14px', color: '#1e293b', marginTop: '4px', lineHeight: 1.5 }}>
                      {hazard.consequenceText}
                    </div>
                  </div>

                  {hazard.potentialOutcome && (
                    <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '6px', borderLeft: '4px solid #dc2626' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase' }}>
                        Worst Credible Outcome (Accident / Excursion / Hull Loss)
                      </div>
                      <div style={{ fontSize: '14px', color: '#1e293b', marginTop: '4px', lineHeight: 1.5 }}>
                        {hazard.potentialOutcome}
                      </div>
                    </div>
                  )}
                </div>

                {/* Metadata Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px', fontSize: '12px' }}>
                  <div>
                    <span style={{ color: '#64748b' }}>Category:</span>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{hazard.categoryName || hazard.categoryCode}</div>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Severity / Likelihood:</span>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>
                      {hazard.severityEstimate} / {hazard.likelihoodEstimate}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Department / Base:</span>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>
                      {hazard.department || 'All Fleets'} {hazard.location ? `(${hazard.location})` : ''}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Next Review Date:</span>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>
                      {hazard.nextReviewDate ? new Date(hazard.nextReviewDate).toLocaleDateString() : 'Not scheduled'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: CAUSES & CONSEQUENCES */}
          {activeTab === 'causes_consequences' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Causes Column */}
              <div style={{ background: '#ffffff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#0f172a' }}>
                  Underlying Causes & Triggers ({hazard.causes?.length || 0})
                </h4>

                <form onSubmit={handleAddCause} style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                  <input
                    type="text"
                    required
                    placeholder="Enter cause description..."
                    value={newCauseText}
                    onChange={(e) => setNewCauseText(e.target.value)}
                    style={{ flex: 1, padding: '7px 10px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  />
                  <select
                    value={newCauseType}
                    onChange={(e) => setNewCauseType(e.target.value as CauseType)}
                    style={{ padding: '7px 8px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  >
                    <option value="HUMAN">Human</option>
                    <option value="TECHNICAL">Technical</option>
                    <option value="ORGANIZATIONAL">Org</option>
                    <option value="ENVIRONMENTAL">Env</option>
                    <option value="PROCEDURAL">Proc</option>
                  </select>
                  <button type="submit" style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '7px 12px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                    +
                  </button>
                </form>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {hazard.causes?.map((c) => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '12px' }}>
                      <div>
                        <span style={{ fontWeight: 600, color: '#334155' }}>[{c.causeType}]</span> {c.causeText}
                      </div>
                      <button onClick={() => handleRemoveCause(c.id)} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer' }}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Consequences Column */}
              <div style={{ background: '#ffffff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#0f172a' }}>
                  Potential Operational Consequences ({hazard.consequences?.length || 0})
                </h4>

                <form onSubmit={handleAddConsequence} style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                  <input
                    type="text"
                    required
                    placeholder="Enter consequence statement..."
                    value={newConsequenceText}
                    onChange={(e) => setNewConsequenceText(e.target.value)}
                    style={{ flex: 1, padding: '7px 10px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  />
                  <select
                    value={newConsequenceSeverity}
                    onChange={(e) => setNewConsequenceSeverity(e.target.value as ConsequenceSeverity)}
                    style={{ padding: '7px 8px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  >
                    <option value="NEGLIGIBLE">Negligible</option>
                    <option value="MINOR">Minor</option>
                    <option value="MAJOR">Major</option>
                    <option value="HAZARDOUS">Hazardous</option>
                    <option value="CATASTROPHIC">Catastrophic</option>
                  </select>
                  <button type="submit" style={{ background: '#ea580c', color: '#fff', border: 'none', padding: '7px 12px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                    +
                  </button>
                </form>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {hazard.consequences?.map((cons) => (
                    <div key={cons.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '12px' }}>
                      <div>
                        <span style={{ fontWeight: 600, color: '#b91c1c' }}>[{cons.severityLevel}]</span> {cons.consequenceText}
                      </div>
                      <button onClick={() => handleRemoveConsequence(cons.id)} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer' }}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: CONTROLS & BARRIERS */}
          {activeTab === 'controls' && (
            <div style={{ background: '#ffffff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#0f172a' }}>
                Active Preventive & Mitigative Safety Barriers ({hazard.controls?.length || 0})
              </h4>

              {/* Add Control Form */}
              <form onSubmit={handleAddControl} style={{ background: '#f8fafc', padding: '16px', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 180px', gap: '10px' }}>
                  <input
                    type="text"
                    required
                    placeholder="Barrier Title (e.g. Daily Airside Sweeper Patrols)"
                    value={newControlTitle}
                    onChange={(e) => setNewControlTitle(e.target.value)}
                    style={{ padding: '8px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  />
                  <select
                    value={newControlType}
                    onChange={(e) => setNewControlType(e.target.value as ControlType)}
                    style={{ padding: '8px 10px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  >
                    <option value="PREVENTIVE">PREVENTIVE Barrier</option>
                    <option value="MITIGATIVE">MITIGATIVE Barrier</option>
                    <option value="RECOVERY">RECOVERY Barrier</option>
                    <option value="DETECTIVE">DETECTIVE Barrier</option>
                  </select>
                  <select
                    value={newControlEffectiveness}
                    onChange={(e) => setNewControlEffectiveness(e.target.value as ControlEffectiveness)}
                    style={{ padding: '8px 10px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  >
                    <option value="SATISFACTORY">SATISFACTORY</option>
                    <option value="PARTIAL">PARTIAL</option>
                    <option value="INEFFECTIVE">INEFFECTIVE</option>
                    <option value="UNDER_EVALUATION">UNDER EVALUATION</option>
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px 100px', gap: '10px' }}>
                  <input
                    type="text"
                    required
                    placeholder="Barrier operational description / procedure reference..."
                    value={newControlDesc}
                    onChange={(e) => setNewControlDesc(e.target.value)}
                    style={{ padding: '8px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  />
                  <input
                    type="text"
                    placeholder="Dept (e.g. Ground Ops)"
                    value={newControlDept}
                    onChange={(e) => setNewControlDept(e.target.value)}
                    style={{ padding: '8px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  />
                  <button type="submit" style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}>
                    Add Barrier
                  </button>
                </div>
              </form>

              {/* Controls List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {hazard.controls?.map((ctrl) => (
                  <div
                    key={ctrl.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: ctrl.isActive ? '#f8fafc' : '#f1f5f9',
                      borderRadius: '6px',
                      border: `1px solid ${ctrl.isActive ? '#cbd5e1' : '#e2e8f0'}`,
                      opacity: ctrl.isActive ? 1 : 0.6,
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>{ctrl.controlTitle}</span>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '3px', background: ctrl.controlType === 'PREVENTIVE' ? '#dbeafe' : '#fef3c7', color: ctrl.controlType === 'PREVENTIVE' ? '#1d4ed8' : '#b45309' }}>
                          {ctrl.controlType}
                        </span>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '3px', background: '#dcfce7', color: '#15803d' }}>
                          {ctrl.effectiveness}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#475569' }}>{ctrl.description}</div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button
                        onClick={() => handleToggleControl(ctrl.id, ctrl.isActive)}
                        style={{
                          background: ctrl.isActive ? '#fee2e2' : '#dcfce7',
                          color: ctrl.isActive ? '#b91c1c' : '#15803d',
                          border: 'none',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {ctrl.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 4: SOURCES LINKAGE */}
          {activeTab === 'sources' && (
            <div style={{ background: '#ffffff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#0f172a' }}>
                Cross-Module Linked Evidence & Sources ({hazard.sources?.length || 0})
              </h4>

              <form onSubmit={handleAddSource} style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <select
                  value={newSourceType}
                  onChange={(e) => setNewSourceType(e.target.value as SourceType)}
                  style={{ padding: '8px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                >
                  <option value="OCCURRENCE">3.1 Occurrence</option>
                  <option value="INVESTIGATION">3.2 Investigation</option>
                  <option value="AUDIT">Safety Audit</option>
                  <option value="MOC">Management of Change</option>
                  <option value="SURVEY">Safety Survey</option>
                </select>
                <input
                  type="text"
                  required
                  placeholder="Reference Identifier (e.g. OCC-2026-00001 or INV-2026-00002)"
                  value={newSourceRef}
                  onChange={(e) => setNewSourceRef(e.target.value)}
                  style={{ flex: 1, padding: '8px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                />
                <input
                  type="text"
                  placeholder="Context / Note"
                  value={newSourceDesc}
                  onChange={(e) => setNewSourceDesc(e.target.value)}
                  style={{ width: '220px', padding: '8px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                />
                <button type="submit" style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                  Link Source
                </button>
              </form>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {hazard.sources?.map((s) => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: '#e0f2fe', color: '#0369a1', fontSize: '11px' }}>
                        {s.sourceType}
                      </span>
                      <span style={{ fontWeight: 600, color: '#0f172a' }}>{s.sourceReferenceId}</span>
                      {s.description && <span style={{ color: '#64748b', fontSize: '12px' }}>— {s.description}</span>}
                    </div>
                    <button onClick={() => handleUnlinkSource(s.id)} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px' }}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 5: PERIODIC REVIEWS */}
          {activeTab === 'reviews' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ background: '#ffffff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#0f172a' }}>
                  Perform Periodic Safety Review & Assessment
                </h4>

                <form onSubmit={handleRecordReview} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                        Review Verdict *
                      </label>
                      <select
                        value={reviewVerdict}
                        onChange={(e) => setReviewVerdict(e.target.value as ReviewVerdict)}
                        style={{ width: '100%', padding: '8px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff' }}
                      >
                        <option value="MAINTAIN_CONTROLS">MAINTAIN CONTROLS (Barriers performing adequately)</option>
                        <option value="REASSESS_RISK">REASSESS RISK (Trigger formal 3.4 Risk Assessment)</option>
                        <option value="ACTION_REQUIRED">ACTION REQUIRED (Under Review, additional CAPA needed)</option>
                        <option value="RETIRE">RETIRE HAZARD (Threat eliminated or no longer applicable)</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                        Next Cycle (Days)
                      </label>
                      <select
                        value={customReviewDays}
                        onChange={(e) => setCustomReviewDays(parseInt(e.target.value, 10))}
                        style={{ width: '100%', padding: '8px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff' }}
                      >
                        <option value={30}>30 Days</option>
                        <option value={60}>60 Days</option>
                        <option value={90}>90 Days</option>
                        <option value={180}>180 Days</option>
                        <option value={365}>365 Days</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                      Review Findings & Technical Comments *
                    </label>
                    <textarea
                      required
                      rows={2}
                      placeholder="Detail operational observations, barrier effectiveness evaluations, or reasons to retire..."
                      value={reviewComments}
                      onChange={(e) => setReviewComments(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="submit" style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                      Record Periodic Review
                    </button>
                  </div>
                </form>
              </div>

              {/* Review History */}
              <div style={{ background: '#ffffff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#0f172a' }}>
                  Review Log History ({hazard.reviews?.length || 0})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {hazard.reviews?.map((r) => (
                    <div key={r.id} style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <div>
                          <span style={{ fontWeight: 700, color: '#0f172a' }}>{r.reviewerName}</span>
                          <span style={{ color: '#64748b', marginLeft: '8px' }}>
                            {new Date(r.reviewDate).toLocaleDateString()}
                          </span>
                        </div>
                        <span style={{ fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: r.verdict === 'RETIRE' ? '#f3e8ff' : '#dcfce7', color: r.verdict === 'RETIRE' ? '#7e22ce' : '#15803d' }}>
                          {r.verdict}
                        </span>
                      </div>
                      <div style={{ color: '#334155' }}>{r.comments}</div>
                      <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>
                        Next scheduled review: {new Date(r.nextReviewDate).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 6: COLLABORATION & NOTES */}
          {activeTab === 'collaboration' && (
            <div style={{ background: '#ffffff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#0f172a' }}>Discussion & Collaboration</h4>
              <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <input
                  type="text"
                  required
                  placeholder="Post comment or operational feedback..."
                  value={commentText}
                  onChange={(e) => setNewCauseText(e.target.value)}
                  style={{ flex: 1, padding: '8px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                />
                <button type="submit" style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                  Post
                </button>
              </form>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {hazard.comments?.map((c) => (
                  <div key={c.id} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '2px' }}>{c.userName}</div>
                    <div style={{ color: '#334155' }}>{c.commentText}</div>
                    <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '2px' }}>{new Date(c.createdAt).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 7: STATUS & AUDIT HISTORY */}
          {activeTab === 'history' && (
            <div style={{ background: '#ffffff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#0f172a' }}>Immutable Status Transition Audit Log</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {hazard.statusHistory?.map((h) => (
                  <div key={h.id} style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <div>
                        <span style={{ fontWeight: 700, color: '#0f172a' }}>{h.fromStatus} $\rightarrow$ {h.toStatus}</span>
                        <span style={{ color: '#64748b', marginLeft: '8px' }}>by {h.changedBy}</span>
                      </div>
                      <span style={{ color: '#94a3b8', fontSize: '11px' }}>{new Date(h.changedAt).toLocaleString()}</span>
                    </div>
                    {h.reason && <div style={{ color: '#334155', fontStyle: 'italic' }}>"{h.reason}"</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
